import { VGeos, ScmResources, SupportedScmCdnBizKeys } from "../resources";
import { logger } from "./shared";
const isScmEnv = () => process.env.BUILD_TYPE !== void 0 && process.env.CDN_BUSINESS_KEY !== void 0;
const isI18nScmEnv = () => isScmEnv() && process.env.BUILD_REGION !== "oci";
const isTtpScmEnv = () => isScmEnv() && process.env.BUILD_REGION === "oci";
const isScmOnlineBuildEnv = () => isScmEnv() && process.env.BUILD_TYPE === "online";
const isScmTestBuildEnv = () => isScmEnv() && process.env.BUILD_TYPE === "test";
const isScmOfflineBuildEnv = () => isScmEnv() && process.env.BUILD_TYPE === "offline";
const getScmCdnCustomPathPrefixFromEnv = () => process.env.CDN_PATH_PREFIX;
const getScmCdnOuterDomainFromEnvByVGeo = (vgeo) => {
  let domain;
  switch (vgeo) {
    case "row":
      domain = process.env.CDN_OUTER_SG;
      break;
    case "eu":
      domain = process.env.CDN_OUTER_EU;
      break;
    case "us":
      domain = process.env.CDN_OUTER_OCI;
      break;
    default:
  }
  return domain;
};
const getScmCdnInternalDomainFromEnvByVGeo = (vgeo) => {
  let domain;
  switch (vgeo) {
    case "row":
      domain = process.env.CDN_INNER_SG;
      break;
    case "eu":
      domain = process.env.CDN_INNER_EU;
      break;
    case "us":
      domain = process.env.CDN_INNER_OCI;
      break;
    default:
  }
  return domain;
};
const getScmCdnBizKeyFromEnv = () => {
  return process.env.CDN_BUSINESS_KEY;
};
const _isInternalScmCdnBiz = () => !getScmCdnOuterDomainFromEnvByVGeo("row") && !getScmCdnOuterDomainFromEnvByVGeo("us");
const _getScmCdnDomainConfig = (_domainConfig) => {
  if (_domainConfig) {
    return _domainConfig;
  }
  const cdnBizKey = getScmCdnBizKeyFromEnv();
  if (cdnBizKey in ScmResources.supportedDomainConfigs) {
    return ScmResources.supportedDomainConfigs[cdnBizKey];
  }
  logger.warn("getPublicPaths", "Current SCM CDN Biz line is not supported, using default upload config");
  if (_isInternalScmCdnBiz()) {
    return ScmResources.defaultInternalDomainConfig;
  }
  return ScmResources.defaultOuterDomainConfig;
};
const _hasEuSupport = () => !!getScmCdnOuterDomainFromEnvByVGeo("eu") || !!getScmCdnInternalDomainFromEnvByVGeo("eu");
const _getScmCdnUploadConfig = (_domainConfig, useRowResourceInEu = true) => {
  const domainConfig = _getScmCdnDomainConfig(_domainConfig);
  return {
    domainConfig,
    supportEU: _hasEuSupport(),
    useRowResourceInEu
  };
};
const _getScmBuildType = () => {
  if (isScmOnlineBuildEnv()) {
    return "onlineBuild";
  } else if (isScmTestBuildEnv()) {
    return "testBuild";
  }
  return "offlineBuild";
};
const _getScmCdnDomainMap = (domainConfig) => {
  const scmBuildType = _getScmBuildType();
  const scmDomainType = domainConfig[scmBuildType];
  const domainMap = {};
  VGeos.forEach((vgeo) => {
    if (scmDomainType === "outerDomain") {
      domainMap[vgeo] = getScmCdnOuterDomainFromEnvByVGeo(vgeo);
    } else {
      domainMap[vgeo] = getScmCdnInternalDomainFromEnvByVGeo(vgeo);
    }
  });
  return domainMap;
};
const _getScmCdnPrefixFromDomainMapByVGeo = (domainMap, vgeo) => {
  const cdnDomain = domainMap[vgeo];
  if (cdnDomain === void 0) {
    const errorMsg = logger.parseErrorMsg("getPublicPaths", `CDN Domain from SCM environment variable for ${vgeo} is undefined`);
    throw new Error(errorMsg);
  }
  const customPrefix = getScmCdnCustomPathPrefixFromEnv();
  if (customPrefix === void 0) {
    const errorMsg = logger.parseErrorMsg("getPublicPaths", "SCM environment variable CDN_PATH_PREFIX is undefined");
    throw new Error(errorMsg);
  }
  return `https://${cdnDomain}/${customPrefix}`;
};
const _getI18nScmPublicPaths = (i18nBuildVGeos, uploadConfig) => {
  const publicPaths = {};
  if (i18nBuildVGeos.length === 0) {
    return publicPaths;
  }
  const { domainConfig, supportEU, useRowResourceInEu = true } = uploadConfig;
  const domainMap = _getScmCdnDomainMap(domainConfig);
  if (supportEU) {
    i18nBuildVGeos.forEach((v) => {
      publicPaths[v] = _getScmCdnPrefixFromDomainMapByVGeo(domainMap, v);
    });
  } else {
    const rowPrefix = _getScmCdnPrefixFromDomainMapByVGeo(domainMap, "row");
    if (i18nBuildVGeos.length === 1 || useRowResourceInEu) {
      i18nBuildVGeos.forEach((v) => {
        publicPaths[v] = rowPrefix;
      });
    } else {
      i18nBuildVGeos.forEach((v) => {
        publicPaths[v] = `${rowPrefix}/${v}`;
      });
    }
  }
  return publicPaths;
};
const _getI18nDynamicPublicPaths = (i18nBuildVGeos) => {
  if (getScmCdnBizKeyFromEnv() !== SupportedScmCdnBizKeys.DefaultInternalCommon) {
    const errorMsg = logger.parseErrorMsg("getI18nDynamicPublicPaths", "CDN 4.0 dynamic domain only works with Default Internal Common business line");
    throw new Error(errorMsg);
  }
  const publicPaths = {};
  if (i18nBuildVGeos.length === 0) {
    return publicPaths;
  }
  const customPrefix = getScmCdnCustomPathPrefixFromEnv();
  if (customPrefix === void 0) {
    const errorMsg = logger.parseErrorMsg("getI18nDynamicPublicPaths", "SCM environment variable CDN_PATH_PREFIX is undefined");
    throw new Error(errorMsg);
  }
  i18nBuildVGeos.forEach((v) => {
    publicPaths[v] = `https://${ScmResources.internalDynamicDomain}/${customPrefix}`;
  });
  return publicPaths;
};
const _getTtpScmPublicPaths = (ttpBuildVGeos, uploadConfig) => {
  const publicPaths = {};
  if (ttpBuildVGeos.length === 0) {
    return publicPaths;
  }
  const { domainConfig } = uploadConfig;
  const domainMap = _getScmCdnDomainMap(domainConfig);
  publicPaths.us = _getScmCdnPrefixFromDomainMapByVGeo(domainMap, "us");
  return publicPaths;
};
const _isI18nScmVGeo = (vgeo) => ScmResources.i18nBuildVGeos.includes(vgeo);
const _isTtpScmVGeo = (vgeo) => ScmResources.ttpBuildVGeos.includes(vgeo);
const getPublicPaths = (buildVGeos, options) => {
  if (!isScmEnv()) {
    const errorMsg = logger.parseErrorMsg("getPublicPaths", "not in SCM Env, BUILD_TYPE or CDN_BUSINESS_KEY is undefined");
    throw new Error(errorMsg);
  }
  const { domainConfig, useRowResourceInEu = true, useDynamicPublicPaths } = options || {};
  const scmCdnUploadConfig = _getScmCdnUploadConfig(domainConfig, useRowResourceInEu);
  if (isTtpScmEnv()) {
    const ttpBuildVGeos = buildVGeos.filter((v) => _isTtpScmVGeo(v));
    return _getTtpScmPublicPaths(ttpBuildVGeos, scmCdnUploadConfig);
  } else {
    const i18nBuildVGeos = buildVGeos.filter((v) => _isI18nScmVGeo(v));
    if (useDynamicPublicPaths) {
      return _getI18nDynamicPublicPaths(i18nBuildVGeos);
    }
    return _getI18nScmPublicPaths(i18nBuildVGeos, scmCdnUploadConfig);
  }
};
const ScmUtils = {
  isScmEnv,
  isI18nEnv: isI18nScmEnv,
  isTtpEnv: isTtpScmEnv,
  isOnlineBuildEnv: isScmOnlineBuildEnv,
  isTestBuildEnv: isScmTestBuildEnv,
  isOfflineBuildEnv: isScmOfflineBuildEnv,
  getCustomPathPrefixFromEnv: getScmCdnCustomPathPrefixFromEnv,
  getOuterDomainFromEnvByVGeo: getScmCdnOuterDomainFromEnvByVGeo,
  getInternalDomainFromEnvByVGeo: getScmCdnInternalDomainFromEnvByVGeo,
  getCdnBizKeyFromEnv: getScmCdnBizKeyFromEnv,
  getPublicPaths
};
export {
  ScmUtils,
  getPublicPaths,
  getScmCdnBizKeyFromEnv,
  getScmCdnCustomPathPrefixFromEnv,
  getScmCdnInternalDomainFromEnvByVGeo,
  getScmCdnOuterDomainFromEnvByVGeo,
  isI18nScmEnv,
  isScmEnv,
  isScmOfflineBuildEnv,
  isScmOnlineBuildEnv,
  isScmTestBuildEnv,
  isTtpScmEnv
};

//# sourceMappingURL=scm.js.map